import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import type { RecipeSummary } from "../../shared/recipes";
import {
  collectKnownTagPaths,
  formatTagPath,
  normalizeRecipeTags,
  recipeMatchesSearchQuery,
  recipeMatchesTagFilter,
  tagPathKey
} from "../../shared/recipe-tags";
import { fetchRecipes } from "../api/recipes";

type RecipeListProps = {
  onNavigate: (path: string) => void;
};

export function RecipeList({ onNavigate }: RecipeListProps): JSX.Element {
  const [recipes, setRecipes] = useState<RecipeSummary[] | undefined>(undefined);
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState<string[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let active = true;
    void fetchRecipes()
      .then((items) => {
        if (active) {
          setRecipes(items);
        }
      })
      .catch(() => {
        if (active) {
          setError("Could not load recipes.");
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const knownTags = useMemo(() => (recipes === undefined ? [] : collectKnownTagPaths(recipes)), [recipes]);

  const filtered = useMemo(() => {
    if (recipes === undefined) {
      return undefined;
    }
    return recipes.filter((recipe) => {
      if (tagFilter !== undefined && !recipeMatchesTagFilter(recipe.tags, tagFilter)) {
        return false;
      }
      return recipeMatchesSearchQuery(recipe, query);
    });
  }, [recipes, query, tagFilter]);

  return (
    <div className="recipes-panel">
      <header className="recipes-header">
        <div>
          <p className="eyebrow">Recipes</p>
          <h2>Your library</h2>
        </div>
        <div className="recipes-header-actions">
          <button className="secondary-action" type="button" onClick={() => { onNavigate("/recipes/import"); }}>
            Import
          </button>
          <button className="primary-action recipes-add" type="button" onClick={() => { onNavigate("/recipes/new"); }}>
            Add recipe
          </button>
        </div>
      </header>

      <label className="field">
        <span className="field-label">Search</span>
        <input
          className="text-input"
          type="search"
          value={query}
          placeholder="Search by title or tag"
          onChange={(event) => { setQuery(event.target.value); }}
        />
      </label>

      {knownTags.length > 0 ? (
        <div className="recipe-tag-filters" role="group" aria-label="Filter by tag">
          {knownTags.map((path) => {
            const active = tagFilter !== undefined && tagPathKey(tagFilter) === tagPathKey(path);
            return (
              <button
                key={formatTagPath(path)}
                type="button"
                className={active ? "recipe-tag-chip active" : "recipe-tag-chip"}
                aria-pressed={active}
                onClick={() => {
                  setTagFilter(active ? undefined : path);
                }}
              >
                {formatTagPath(path)}
              </button>
            );
          })}
        </div>
      ) : null}

      {error !== undefined ? <p className="error-text" role="alert">{error}</p> : null}

      {filtered === undefined && error === undefined ? <p className="muted-text">Loading recipes…</p> : null}

      {filtered?.length === 0 ? (
        <p className="muted-text">No recipes yet. Add your first recipe to get started.</p>
      ) : null}

      {filtered !== undefined && filtered.length > 0 ? (
        <ul className="recipe-list">
          {filtered.map((recipe) => {
            const tags = normalizeRecipeTags(recipe.tags);
            return (
            <li key={recipe.id}>
              <button
                className="recipe-list-item privacy-private"
                type="button"
                onClick={() => { onNavigate(`/recipes/${recipe.id}`); }}
              >
                <span className="recipe-list-title">{recipe.title}</span>
                <span className="recipe-list-meta">
                  {recipe.stepCount} steps · Serves {recipe.servings}
                  {recipe.estimatedMinutes !== undefined ? ` · ~${String(recipe.estimatedMinutes)} min` : ""}
                </span>
                {tags.length > 0 ? (
                  <span className="recipe-list-tags">
                    {tags.map((tag) => (
                      <span key={formatTagPath(tag.path)} className="recipe-tag-chip readonly">
                        {formatTagPath(tag.path)}
                      </span>
                    ))}
                  </span>
                ) : null}
              </button>
            </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
